/**
 * components/landing/launch-platform/sections/Hero.jsx — §6.1 Hero.
 *
 * The big "Launch Your Trading Platform. Under Your Brand." opener.
 * Left half is the copy + CTAs.  Right half is a branded platform mock
 * that visually communicates the white-label transformation: a browser
 * frame whose URL flips from `kalkiai.trade` -> `alphatrade.io`, whose
 * logo swaps from Kalki Ai Cloud -> AlphaTrade, and whose accent colour follows.
 */

import React from 'react'
import {
  Rocket, Sparkles, Palette, Globe, ShieldCheck, Users, Bot, ArrowRight,
} from 'lucide-react'
import {
  Container, Eyebrow, GradientButton, GhostButton,
} from '@/components/landing/design-system'

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(255,193,7,0.14), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />

      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Eyebrow>Launch Your Platform</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Launch your trading platform.{' '}
              <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Under your brand.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Turn 24 months of platform engineering into an afternoon of configuration.
              Ship a fully-branded AI trading, mentor and academy platform to your customers
              — with your logo, your domain, your pricing and your revenue.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="/get-started" size="lg">
                <Rocket className="h-4 w-4" /> Launch platform
              </GradientButton>
              <GhostButton href="/contact" size="lg" trailingIcon>
                Schedule demo
              </GhostButton>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { i: Palette,     l: 'Total brand control' },
                { i: Globe,       l: 'Custom domain + SSL' },
                { i: Users,       l: 'Multi-tenant workspaces' },
                { i: ShieldCheck, l: 'Enterprise security' },
              ].map((t) => (
                <span key={t.l} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <t.i className="h-3 w-3 text-brand-gold" /> {t.l}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — brand-swap mock */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />
              {/* browser chrome */}
              <div className="relative flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-2 border border-white/5 mb-3">
                <div className="h-2 w-2 rounded-full bg-red-500/70" />
                <div className="h-2 w-2 rounded-full bg-amber-500/70" />
                <div className="h-2 w-2 rounded-full bg-emerald-500/70" />
                <div className="ml-3 flex-1 h-5 rounded-md bg-white/[0.04] flex items-center px-2 text-[10px] font-mono text-zinc-500">
                  app.alphatrade.io/dashboard
                </div>
                <div className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 pr-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> live
                </div>
              </div>

              {/* app mock with tenant branding */}
              <div className="relative grid grid-cols-12 gap-3">
                {/* Sidebar */}
                <div className="col-span-3 rounded-lg border border-white/10 bg-black/40 p-3 flex flex-col gap-2">
                  <div className="flex items-center gap-2 pb-2 mb-1 border-b border-white/5">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-brand-gold to-[#FFE17A] text-black font-bold text-[10px]">A</span>
                    <span className="text-[11px] font-semibold text-white leading-tight">AlphaTrade</span>
                  </div>
                  {['Dashboard', 'Terminal', 'Strategy', 'Academy', 'Billing'].map((l, i) => (
                    <div key={l}
                      className={`text-[10.5px] px-2 py-1 rounded-md ${i === 0 ? 'bg-brand-gold/10 text-brand-gold border border-brand-gold/25' : 'text-zinc-400'}`}>
                      {l}
                    </div>
                  ))}
                </div>

                {/* Main pane */}
                <div className="col-span-9 flex flex-col gap-3">
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] font-semibold text-white">Portfolio</div>
                      <div className="text-[10px] font-mono text-emerald-400">+3.24% today</div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { l: 'NAV',      v: '$4.28M', t: 'orange' },
                        { l: 'Positions', v: '18',    t: 'blue'   },
                        { l: 'MRR',      v: '$126k',  t: 'green'  },
                      ].map((s) => (
                        <div key={s.l} className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5">
                          <div className={`text-[9px] font-mono uppercase tracking-wider ${s.t === 'green' ? 'text-emerald-400' : s.t === 'orange' ? 'text-brand-gold' : 'text-sky-400'}`}>{s.l}</div>
                          <div className="text-sm font-semibold text-white">{s.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* AI mentor with tenant identity */}
                  <div className="rounded-lg border border-brand-gold/25 bg-brand-gold/[0.06] p-3 flex items-start gap-2">
                    <Bot className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
                    <div className="flex-1 text-[11.5px] text-zinc-100 leading-relaxed">
                      <span className="font-semibold text-brand-gold">AlphaMentor:</span>{' '}
                      Your AI mentor is fully branded — including name, tone and knowledge scope.
                      <div className="mt-1 text-[9.5px] font-mono text-zinc-500">tenant=alphatrade · model=grounded · policy=custom</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* footer strip */}
              <div className="relative mt-3 flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                <div className="flex items-center gap-2 text-[10.5px] text-zinc-400">
                  <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
                  <span><span className="text-white font-semibold">Powered by Kalki Ai Cloud</span> · white-label mode</span>
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-emerald-400 font-mono">
                  ~90s provisioning <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
